// src/pages/EnqueteMenage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CardMedia
} from '@mui/material';
import Footer from '../navbar/Footer';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

function EnqueteMenage() {
  const [visibleCount, setVisibleCount] = useState(2);
  const [filteredBulletins, setFilteredBulletins] = useState([]);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [allBulletins, setAllBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'rem', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le tri et le filtre
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setAllBulletins(sorted);
          setFilteredBulletins(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Génération des années uniques à partir des données
  const uniqueYears = useMemo(() => {
    const years = allBulletins.map(bulletin => bulletin.date.getFullYear().toString());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [allBulletins]);

  // Filtrage par année
  const filterBulletins = (bulletins, year) => {
    if (year === 'Tous') return bulletins;
    return bulletins.filter(b => b.date.getFullYear().toString() === year);
  };

  // Mise à jour du filtre lors du changement d'année
  useEffect(() => {
    if (allBulletins.length) {
      const filtered = filterBulletins(allBulletins, selectedYear);
      setFilteredBulletins(filtered);
      setVisibleCount(2); // réinitialiser le nombre visible
    }
  }, [selectedYear, allBulletins]);

  const handleShowMore = () => {
    setVisibleCount(prevCount => prevCount + 2);
  };

  const handleFilter = (year) => {
    setSelectedYear(year);
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C', marginBottom: '20px' }}>
            Rapports des enquêtes ménages
          </Typography>
          <Typography variant="body1">Chargement des données...</Typography>
        </Container>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C', marginBottom: '20px' }}>
            Rapports des enquêtes ménages
          </Typography>
          <Typography variant="body1" color="error">{error}</Typography>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
      <NavigationBar />
      <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
        <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C', marginBottom: '20px' }}>
          Rapports des enquêtes ménages
        </Typography>
        <Typography variant="body1" align="left" gutterBottom style={{ marginBottom: '20px' }}>
          Cette page met à votre disposition une sélection des rapports d'enquête menés sur les conditions de vie des ménages burundais. Ces documents fournissent des analyses approfondies des défis socio-économiques auxquels sont confrontées les familles, ainsi que des recommandations pour améliorer leur bien-être.
          <br /><br />
          En naviguant à travers ces rapports, vous découvrirez des informations précieuses sur la pauvreté, l'éducation, l'accès aux soins de santé et bien plus encore, afin de mieux comprendre le contexte de développement du Burundi et d'orienter les actions futures.
        </Typography>

        <Grid container spacing={1} style={{ marginTop: '20px' }}>
          <Grid item xs={12} sm={6} md={12}>
            <FormControl fullWidth style={{ marginLeft: '0px', width: '300px', marginBottom: '20px' }}>
              <InputLabel id="year-select-label">Sélectionnez une année de publication</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={selectedYear}
                onChange={(e) => handleFilter(e.target.value)}
                label="Sélectionnez une année de publication"
                style={{ width: '100%' }}
              >
                <MenuItem value="Tous">Tous les rapports</MenuItem>
                {uniqueYears.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={1} style={{ marginTop: '10px' }}>
          {filteredBulletins.slice(0, visibleCount).map((indice, index) => (
            <Grid item xs={12} sm={12} key={index}>
              <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex' }}>
                  <a
                    href={indice.link}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CardMedia
                      component="img"
                      sx={{ width: 91, height: 140, m: 2 }}
                      image={indice.image}
                      alt={indice.title}
                    />
                  </a>
                  <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                    <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      <a
                        href={indice.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'green' }}
                      >
                        <DownloadIcon /> {indice.title}
                      </a>
                    </Typography>
                    <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                      {indice.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Publié en {moment(indice.date).format('MMMM YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {visibleCount < filteredBulletins.length && (
          <Button onClick={handleShowMore} style={{ marginTop: '20px', backgroundColor: '#388E3C', color: 'white' }}>
            Plus de rapports
          </Button>
        )}
      </Container>
      <br />
      <br />
      <Footer />
    </div>
  );
}

export default EnqueteMenage;